import React from 'react';
import {IntlShape} from 'react-intl';
import {HelpCenterLinks} from '../../HelpCenterLinks';
import {type TooltipFormulaItem} from '@/components/TooltipFormulaRenderer';
import {createProjectPortfolioReportTooltip as createTooltip} from './ProjectPortfolioReportTooltip';

type FormatMessage = IntlShape['formatMessage'];

/* Baseline */
const BaselineRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'baseline.baseline_revenue'}),
		description: formatMessage({id: 'project_portfolio_report.baseline.revenue.tooltip.description'}),
	},
];

const BaselineBillableValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.baseline_billable_value_of_service'}),
		description: formatMessage({id: 'project_portfolio_report.baseline.billable_value_of_service.tooltip.description'}),
		details: [
			formatMessage({id: 'baseline.hours'}),
			'x',
			formatMessage({id: 'baseline.rate_per_hour'}),
			'+',
			formatMessage({id: 'project_budget.revenue_from_baseline_expenses'}),
		],
	},
];

const BaselineCostTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'baseline.baseline_cost'}),
		description: formatMessage({id: 'project_portfolio_report.baseline.cost.tooltip.description'}),
		details: [
			formatMessage({id: 'baseline.hours'}),
			'x',
			formatMessage({id: 'common.cost_per_hour'}),
			'+',
			formatMessage({id: 'baseline.expenses_cost'}),
		],
	},
];

const BaselineProfitTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'baseline.baseline_profit'}),
		description: formatMessage({id: 'project_portfolio_report.baseline.profit.tooltip.description'}),
		details: [formatMessage({id: 'baseline.baseline_revenue'}), '-', formatMessage({id: 'project_budget.baseline_cost'})],
	},
];

const BaselineMarginTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'baseline.baseline_margin'}),
		description: formatMessage({id: 'project_portfolio_report.baseline.margin.tooltip.description'}),
		details: [formatMessage({id: 'baseline.baseline_profit'}), '/', formatMessage({id: 'baseline.baseline_revenue'})],
	},
];

/* Estimated */
const EstimatedRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.estimated_revenue'}),
		description: formatMessage(
			{id: 'project_portfolio_report.estimated.revenue.tooltip.description'},
			{link: <HelpCenterLinks.Revenue />}
		),
	},
];

const EstimatedBillableValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.estimated_billable_value_of_service'}),
		description: formatMessage({id: 'project_portfolio_report.estimated.billable_value_of_service.tooltip.description'}),
		details: [
			formatMessage({id: 'common.estimated_hours'}),
			'x',
			formatMessage({id: 'common.rate'}),
			'+',
			formatMessage({id: 'common.price_planned_expenses'}),
		],
	},
];

const EstimatedCostTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.estimated_cost'}),
		description: formatMessage({id: 'project_portfolio_report.estimated.cost.tooltip.description'}),
		details: [
			formatMessage({id: 'common.estimated_hours'}),
			'x',
			formatMessage({id: 'common.internal_hourly_cost'}),
			'+',
			formatMessage({id: 'common.cost_of_planned_expenses'}),
		],
	},
];

const EstimatedProfitTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.estimated_profit'}),
		description: formatMessage({id: 'project_portfolio_report.estimated.profit.tooltip.description'}),
		details: [formatMessage({id: 'common.estimated_revenue'}), '-', formatMessage({id: 'common.estimated_cost'})],
	},
];

const EstimatedMarginTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.estimated_margin'}),
		description: formatMessage({id: 'project_portfolio_report.estimated.margin.tooltip.description'}),
		details: [formatMessage({id: 'common.estimated_profit'}), '/', formatMessage({id: 'common.estimated_revenue'})],
	},
];

/* Actuals */
const ActualRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.actual_revenue'}),
		description: formatMessage(
			{id: 'project_portfolio_report.actual.revenue.tooltip.description'},
			{link: <HelpCenterLinks.Revenue />}
		),
	},
];

const ActualBillableValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.actual_billable_value_of_service'}),
		description: formatMessage({id: 'project_portfolio_report.actual.billable_value_of_service.tooltip.description'}),
		details: [
			formatMessage({id: 'common.actual_billable_hours'}),
			'x',
			formatMessage({id: 'common.rate'}),
			'+',
			formatMessage({id: 'common.price_of_past_expenses'}),
		],
	},
];

const ActualCostTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.actual_cost'}),
		description: formatMessage({id: 'project_portfolio_report.actual.cost.tooltip.description'}),
		details: [
			formatMessage({id: 'common.actual_hours'}),
			'x',
			formatMessage({id: 'common.internal_hourly_cost'}),
			'+',
			formatMessage({id: 'common.cost_of_past_expenses'}),
		],
	},
];

const ActualProfitTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.actual_profit'}),
		description: formatMessage({id: 'project_portfolio_report.actual.profit.tooltip.description'}),
		details: [formatMessage({id: 'common.actual_revenue'}), '-', formatMessage({id: 'common.actual_cost'})],
	},
];

const ActualMarginTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.actual_margin'}),
		description: formatMessage({id: 'project_portfolio_report.actual.margin.tooltip.description'}),
		details: [formatMessage({id: 'common.actual_profit'}), '/', formatMessage({id: 'common.actual_revenue'})],
	},
];

/* Forecast to Complete / Remaining Work */
const RemainingWorkRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.remaining_work_revenue'}),
		description: formatMessage(
			{id: 'project_portfolio_report.remaining_work.revenue.tooltip.description'},
			{link: <HelpCenterLinks.Revenue />}
		),
	},
];

const RemainingWorkBillableValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.remaining_work_billable_value_of_service'}),
		description: formatMessage({
			id: 'project_portfolio_report.remaining_work.billable_value_of_service.tooltip.description',
		}),
		details: [
			formatMessage({id: 'common.remaining_work_billable_hours'}),
			'x',
			formatMessage({id: 'common.rate'}),
			'+',
			formatMessage({id: 'common.price_of_future_expenses'}),
		],
	},
];

const RemainingWorkCostTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.remaining_work_cost'}),
		description: formatMessage({id: 'project_portfolio_report.remaining_work.cost.tooltip.description'}),
		details: [
			formatMessage({id: 'common.remaining_work_hours'}),
			'x',
			formatMessage({id: 'common.internal_hourly_cost'}),
			'+',
			formatMessage({id: 'common.cost_of_future_expenses'}),
		],
	},
];

const RemainingWorkProfitTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.remaining_work_profit'}),
		description: formatMessage({id: 'project_portfolio_report.remaining_work.profit.tooltip.description'}),
		details: [formatMessage({id: 'common.remaining_work_revenue'}), '-', formatMessage({id: 'common.remaining_work_cost'})],
	},
];

const RemainingWorkMarginTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.remaining_work_margin'}),
		description: formatMessage({id: 'project_portfolio_report.remaining_work.margin.tooltip.description'}),
		details: [
			formatMessage({id: 'common.remaining_work_profit'}),
			'/',
			formatMessage({id: 'common.remaining_work_revenue'}),
		],
	},
];

/* Forecasted / Projected Total */
const ProjectedTotalRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_total_revenue'}),
		description: formatMessage(
			{id: 'project_portfolio_report.projected_total.revenue.tooltip.description'},
			{link: <HelpCenterLinks.Revenue />}
		),
		details: [formatMessage({id: 'common.actual_revenue'}), '+', formatMessage({id: 'common.remaining_work_revenue'})],
	},
];

const ProjectedTotalBillableValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_total_billable_value_of_service'}),
		description: formatMessage({
			id: 'project_portfolio_report.projected_total.billable_value_of_service.tooltip.description',
		}),
		details: [
			formatMessage({id: 'common.actual_billable_value_of_service'}),
			'+',
			formatMessage({id: 'common.remaining_work_billable_value_of_service'}),
		],
	},
];

const ProjectedTotalCostTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_total_cost'}),
		description: formatMessage({id: 'project_portfolio_report.projected_total.cost.tooltip.description'}),
		details: [formatMessage({id: 'common.actual_cost'}), '+', formatMessage({id: 'common.remaining_work_cost'})],
	},
];

const ProjectedTotalProfitTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_total_profit'}),
		description: formatMessage({id: 'project_portfolio_report.projected_total.profit.tooltip.description'}),
		details: [
			formatMessage({id: 'common.projected_total_revenue'}),
			'-',
			formatMessage({id: 'common.projected_total_cost'}),
		],
	},
];

const ProjectedTotalMarginTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_total_margin'}),
		description: formatMessage({id: 'project_portfolio_report.projected_total.margin.tooltip.description'}),
		details: [
			formatMessage({id: 'common.projected_total_profit'}),
			'/',
			formatMessage({id: 'common.projected_total_revenue'}),
		],
	},
];

/* Invoiced */
const InvoicedInvoicedTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.invoiced'}),
		description: formatMessage({id: 'project_portfolio_report.invoiced.invoiced.tooltip.description'}),
	},
];

const InvoicedInvoicePaidTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.invoice.paid'}),
		description: formatMessage({id: 'project_portfolio_report.invoiced.invoice_paid.tooltip.description'}),
	},
];

/* Revenue Recognition */
const RevenueRecognitionFromLockedMonthsTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.revenue_recognition_from_locked_months'}),
		description: formatMessage(
			{id: 'project_portfolio_report.revenue_recognition.from_locked_months.tooltip.description'},
			{link: <HelpCenterLinks.RevenueRecognition />}
		),
	},
];

const RevenueRecognitionFromOpenMonthsTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.revenue_recognition_from_open_months'}),
		description: formatMessage(
			{id: 'project_portfolio_report.revenue_recognition.from_open_months.tooltip.description'},
			{link: <HelpCenterLinks.RevenueRecognition />}
		),
	},
];

/* Fixed Price */
const FixedPriceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.fixed_price'}),
		description: formatMessage({id: 'project_portfolio_report.fixed_price.tooltip.description'}),
		details: [
			formatMessage({id: 'common.fixed_price'}),
			'+',
			formatMessage({id: 'common.price_of_expenses_billable_on_top_of_fixed_price'}),
		],
	},
];

/* Estimated vs Actual */
const EstimatedVsActualRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.estimated_vs_actual_revenue'}),
		description: formatMessage({id: 'project_portfolio_report.estimated_vs_actual.revenue.tooltip.description'}),
		details: [formatMessage({id: 'common.estimated_revenue'}), '-', formatMessage({id: 'common.actual_revenue'})],
	},
];

const EstimatedVsActualCostTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.estimated_vs_actual_cost'}),
		description: formatMessage({id: 'project_portfolio_report.estimated_vs_actual.cost.tooltip.description'}),
		details: [formatMessage({id: 'common.estimated_cost'}), '-', formatMessage({id: 'common.actual_cost'})],
	},
];

const EstimatedVsActualProfitTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.estimated_vs_actual_profit'}),
		description: formatMessage({id: 'project_portfolio_report.estimated_vs_actual.profit.tooltip.description'}),
		details: [formatMessage({id: 'common.estimated_profit'}), '-', formatMessage({id: 'common.actual_profit'})],
	},
];

const EstimatedVsActualBillableValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.estimated_vs_actual_billable_value_of_service'}),
		description: formatMessage({
			id: 'project_portfolio_report.estimated_vs_actual.billable_value_of_service.tooltip.description',
		}),
		details: [
			formatMessage({id: 'common.estimated_billable_value_of_service'}),
			'-',
			formatMessage({id: 'common.actual_billable_value_of_service'}),
		],
	},
];

/* Estimated vs Projected Total */
const EstimatedVsProjectedTotalRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.estimated_vs_projected_total_revenue'}),
		description: formatMessage({id: 'project_portfolio_report.estimated_vs_projected_total.revenue.tooltip.description'}),
		details: [formatMessage({id: 'common.estimated_revenue'}), '-', formatMessage({id: 'common.projected_total_revenue'})],
	},
];

const EstimatedVsProjectedTotalCostTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.estimated_vs_projected_total_cost'}),
		description: formatMessage({id: 'project_portfolio_report.estimated_vs_projected_total.cost.tooltip.description'}),
		details: [formatMessage({id: 'common.estimated_cost'}), '-', formatMessage({id: 'common.projected_total_cost'})],
	},
];

const EstimatedVsProjectedTotalProfitTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.estimated_vs_projected_total_profit'}),
		description: formatMessage({id: 'project_portfolio_report.estimated_vs_projected_total.profit.tooltip.description'}),
		details: [formatMessage({id: 'common.estimated_profit'}), '-', formatMessage({id: 'common.projected_total_profit'})],
	},
];

const EstimatedVsProjectedTotalBillableValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'project_budget.estimated_vs_projected_total_billable_value_of_service'}),
		description: formatMessage({
			id: 'project_portfolio_report.estimated_vs_projected_total.billable_value_of_service.tooltip.description',
		}),
		details: [
			formatMessage({id: 'common.estimated_billable_value_of_service'}),
			'-',
			formatMessage({id: 'common.projected_total_billable_value_of_service'}),
		],
	},
];

/* Over / Under Servicing */
const OverUnderServicingProjectedTotalRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_total_revenue'}),
		description: formatMessage(
			{id: 'project_portfolio_report.over_under_servicing.projected_total_revenue.tooltip.description'},
			{link: <HelpCenterLinks.RevenueRecognition />}
		),
		details: [formatMessage({id: 'common.actual_revenue'}), '+', formatMessage({id: 'common.remaining_work_revenue'})],
	},
];

const OverUnderServicingProjectedTotalBillableValueOfServiceTooltipItems = (
	formatMessage: FormatMessage
): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_total_billable_value_of_service'}),
		description: formatMessage({
			id: 'project_portfolio_report.over_under_servicing.projected_total_billable_value_of_service.tooltip.description',
		}),
		details: [
			formatMessage({id: 'common.actual_billable_value_of_service'}),
			'+',
			formatMessage({id: 'common.remaining_work_billable_value_of_service'}),
		],
	},
];

const OverUnderServicingProjectedTotalNonBillableValueOfServiceTooltipItems = (
	formatMessage: FormatMessage
): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_total_non_billable_value_of_service'}),
		description: formatMessage({
			id: 'project_portfolio_report.over_under_servicing.projected_total_non_billable_value_of_service.tooltip.description',
		}),
		details: [
			formatMessage({id: 'common.projected_total_non_billable_hours'}),
			'x',
			formatMessage({id: 'common.rate'}),
			'+',
			formatMessage({id: 'common.cost_of_non_billable_expenses'}),
		],
	},
];

const OverUnderServicingProjectedTotalValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_total_value_of_service'}),
		description: formatMessage({
			id: 'project_portfolio_report.over_under_servicing.projected_total_value_of_service.tooltip.description',
		}),
		details: [
			formatMessage({id: 'common.projected_total_hours'}),
			'x',
			formatMessage({id: 'common.rate'}),
			'+',
			formatMessage({id: 'common.cost_of_non_billable_expenses'}),
		],
	},
];

const OverUnderServicingProjectedRemainingBudgetTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_remaining_budget'}),
		description: formatMessage({
			id: 'project_portfolio_report.over_under_servicing.projected_remaining_budget.tooltip.description',
		}),
		details: [
			formatMessage({id: 'common.projected_total_revenue'}),
			'-',
			formatMessage({id: 'common.projected_total_billable_value_of_service'}),
		],
	},
];

const OverUnderServicingProjectedOverUnderServicingTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_over_under_servicing'}),
		description: formatMessage({
			id: 'project_portfolio_report.over_under_servicing.projected_over_under_servicing.tooltip.description',
		}),
		details: [
			formatMessage({id: 'common.projected_total_revenue'}),
			'-',
			formatMessage({id: 'common.projected_total_value_of_service'}),
		],
	},
];

/* Accrued/Deferred */
const AccruedDeferredInvoicedTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.invoiced'}),
		description: formatMessage({id: 'project_portfolio_report.accrued_deferred.invoiced.tooltip.description'}),
	},
];

const AccruedDeferredRevenueRecognitionFromLockedMonthsTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.revenue_recognition_from_locked_months'}),
		description: formatMessage(
			{id: 'project_portfolio_report.accrued_deferred.revenue_recognition_from_locked_months.tooltip.description'},
			{link: <HelpCenterLinks.RevenueRecognition />}
		),
	},
];

const AccruedDeferredAccruedDeferredTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.accrued_deferred'}),
		description: formatMessage({id: 'project_portfolio_report.accrued_deferred.accrued_deferred.tooltip.description'}),
		details: [
			formatMessage({id: 'common.invoiced'}),
			'-',
			formatMessage({id: 'common.revenue_recognition_from_locked_months'}),
		],
	},
];

/* Retainer Period Target */
const RetainerPeriodTargetTimeTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.retainer_period_target_minus_time'}),
		description: formatMessage({
			id: 'project_portfolio_report.retainer_period_target.retainer_period_target_minus_time.tooltip.description',
		}),
	},
];

const RetainerPeriodTargetPriceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.retainer_period_target_minus_price'}),
		description: formatMessage({
			id: 'project_portfolio_report.retainer_period_target.retainer_period_target_minus_price.tooltip.description',
		}),
	},
];

/* Project Overview */
const ProjectOverviewPriorityLevelTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.priority_level'}),
		description: formatMessage({id: 'project_portfolio_report.project_overview.priority_level.tooltip.description'}),
	},
];

const ProjectOverviewWinProbabilityTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.win_probability'}),
		description: formatMessage({id: 'project_portfolio_report.project_overview.win_probability.tooltip.description'}),
	},
];

const ProjectOverviewProjectStartDateTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.project_start_date'}),
		description: formatMessage({id: 'project_portfolio_report.project_overview.project_start_date.tooltip.description'}),
	},
];

const ProjectOverviewProjectEndDateTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.project_end_date'}),
		description: formatMessage({id: 'project_portfolio_report.project_overview.project_end_date.tooltip.description'}),
	},
];

const ProjectOverviewClientTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.client'}),
		description: formatMessage({id: 'project_portfolio_report.project_overview.client.tooltip.description'}),
	},
];

const ProjectOverviewStatusTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.status'}),
		description: formatMessage({id: 'project_portfolio_report.project_overview.status.tooltip.description'}),
	},
];

const ProjectOverviewStatusDescriptionTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.statusDescription'}),
		description: formatMessage({id: 'project_portfolio_report.project_overview.status_description.tooltip.description'}),
	},
];

const ProjectOverviewProjectOwnerTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.project_owner_capital'}),
		description: formatMessage({id: 'project_portfolio_report.project_overview.project_owner.tooltip.description'}),
	},
];

const ProjectOverviewStageTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.project_stage'}),
		description: formatMessage({id: 'project_portfolio_report.project_overview.project_stage.tooltip.description'}),
	},
];

const ProjectOverviewBudgetTypeTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.budget_type'}),
		description: formatMessage({id: 'project_portfolio_report.project_overview.budget_type.tooltip.description'}),
	},
];

const ProjectOverviewRateCardTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.rate_card'}),
		description: formatMessage({id: 'project_portfolio_report.project_overview.rate_card.tooltip.description'}),
	},
];

const ProjectOverviewProjectProgressTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.project_progress'}),
		description: formatMessage({id: 'project_portfolio_report.project_overview.project_progress.tooltip.description'}),
		details: [
			formatMessage({id: 'common.time_registered_v2'}),
			'/',
			'(',
			formatMessage({id: 'common.time_registered_v2'}),
			'+',
			formatMessage({id: 'common.time_remaining_on_unfinished_tasks'}),
			')',
		],
	},
];

/* Project Hours */
const ProjectHoursBaselineHoursTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'baseline.hours'}),
		description: formatMessage({id: 'project_portfolio_report.project_hours.baseline_hours.tooltip.description'}),
	},
];

const ProjectHoursEstimatedHoursTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.estimated_hours'}),
		description: formatMessage({id: 'project_portfolio_report.project_hours.estimated_hours.tooltip.description'}),
	},
];

const ProjectHoursActualHoursTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.actual_hours'}),
		description: formatMessage({id: 'project_portfolio_report.project_hours.actual_hours.tooltip.description'}),
	},
];

const ProjectHoursRemainingWorkHoursTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.remaining_work_hours'}),
		description: formatMessage({id: 'project_portfolio_report.project_hours.remaining_work_hours.tooltip.description'}),
	},
];

const ProjectHoursProjectedTotalHoursTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_total_hours'}),
		description: formatMessage({id: 'project_portfolio_report.project_hours.projected_total_hours.tooltip.description'}),
		details: [formatMessage({id: 'common.actual_hours'}), '+', formatMessage({id: 'common.remaining_work_hours'})],
	},
];

export const ProjectPortfolioReportHeadersTooltips = {
	BaselineRevenueTooltip: createTooltip(BaselineRevenueTooltipItems),
	BaselineBillableValueOfServiceTooltip: createTooltip(BaselineBillableValueOfServiceTooltipItems),
	BaselineCostTooltip: createTooltip(BaselineCostTooltipItems),
	BaselineProfitTooltip: createTooltip(BaselineProfitTooltipItems),
	BaselineMarginTooltip: createTooltip(BaselineMarginTooltipItems),

	EstimatedRevenueTooltip: createTooltip(EstimatedRevenueTooltipItems),
	EstimatedBillableValueOfServiceTooltip: createTooltip(EstimatedBillableValueOfServiceTooltipItems),
	EstimatedCostTooltip: createTooltip(EstimatedCostTooltipItems),
	EstimatedProfitTooltip: createTooltip(EstimatedProfitTooltipItems),
	EstimatedMarginTooltip: createTooltip(EstimatedMarginTooltipItems),

	ActualRevenueTooltip: createTooltip(ActualRevenueTooltipItems),
	ActualBillableValueOfServiceTooltip: createTooltip(ActualBillableValueOfServiceTooltipItems),
	ActualCostTooltip: createTooltip(ActualCostTooltipItems),
	ActualProfitTooltip: createTooltip(ActualProfitTooltipItems),
	ActualMarginTooltip: createTooltip(ActualMarginTooltipItems),

	RemainingWorkRevenueTooltip: createTooltip(RemainingWorkRevenueTooltipItems),
	RemainingWorkBillableValueOfServiceTooltip: createTooltip(RemainingWorkBillableValueOfServiceTooltipItems),
	RemainingWorkCostTooltip: createTooltip(RemainingWorkCostTooltipItems),
	RemainingWorkProfitTooltip: createTooltip(RemainingWorkProfitTooltipItems),
	RemainingWorkMarginTooltip: createTooltip(RemainingWorkMarginTooltipItems),

	ProjectedTotalRevenueTooltip: createTooltip(ProjectedTotalRevenueTooltipItems),
	ProjectedTotalBillableValueOfServiceTooltip: createTooltip(ProjectedTotalBillableValueOfServiceTooltipItems),
	ProjectedTotalCostTooltip: createTooltip(ProjectedTotalCostTooltipItems),
	ProjectedTotalProfitTooltip: createTooltip(ProjectedTotalProfitTooltipItems),
	ProjectedTotalMarginTooltip: createTooltip(ProjectedTotalMarginTooltipItems),

	InvoicedInvoicedTooltip: createTooltip(InvoicedInvoicedTooltipItems),
	InvoicedInvoicePaidTooltip: createTooltip(InvoicedInvoicePaidTooltipItems),

	RevenueRecognitionFromLockedMonthsTooltip: createTooltip(RevenueRecognitionFromLockedMonthsTooltipItems),
	RevenueRecognitionFromOpenMonthsTooltip: createTooltip(RevenueRecognitionFromOpenMonthsTooltipItems),
	FixedPriceTooltip: createTooltip(FixedPriceTooltipItems),

	EstimatedVsActualRevenueTooltip: createTooltip(EstimatedVsActualRevenueTooltipItems),
	EstimatedVsActualCostTooltip: createTooltip(EstimatedVsActualCostTooltipItems),
	EstimatedVsActualProfitTooltip: createTooltip(EstimatedVsActualProfitTooltipItems),
	EstimatedVsActualBillableValueOfServiceTooltip: createTooltip(EstimatedVsActualBillableValueOfServiceTooltipItems),

	EstimatedVsProjectedTotalRevenueTooltip: createTooltip(EstimatedVsProjectedTotalRevenueTooltipItems),
	EstimatedVsProjectedTotalCostTooltip: createTooltip(EstimatedVsProjectedTotalCostTooltipItems),
	EstimatedVsProjectedTotalProfitTooltip: createTooltip(EstimatedVsProjectedTotalProfitTooltipItems),
	EstimatedVsProjectedTotalBillableValueOfServiceTooltip: createTooltip(
		EstimatedVsProjectedTotalBillableValueOfServiceTooltipItems
	),

	OverUnderServicingProjectedTotalRevenueTooltip: createTooltip(OverUnderServicingProjectedTotalRevenueTooltipItems),
	OverUnderServicingProjectedTotalBillableValueOfServiceTooltip: createTooltip(
		OverUnderServicingProjectedTotalBillableValueOfServiceTooltipItems
	),
	OverUnderServicingProjectedTotalNonBillableValueOfServiceTooltip: createTooltip(
		OverUnderServicingProjectedTotalNonBillableValueOfServiceTooltipItems
	),
	OverUnderServicingProjectedTotalValueOfServiceTooltip: createTooltip(
		OverUnderServicingProjectedTotalValueOfServiceTooltipItems
	),
	OverUnderServicingProjectedRemainingBudgetTooltip: createTooltip(OverUnderServicingProjectedRemainingBudgetTooltipItems),
	OverUnderServicingProjectedOverUnderServicingTooltip: createTooltip(
		OverUnderServicingProjectedOverUnderServicingTooltipItems
	),

	AccruedDeferredInvoicedTooltip: createTooltip(AccruedDeferredInvoicedTooltipItems),
	AccruedDeferredRevenueRecognitionFromLockedMonthsTooltip: createTooltip(
		AccruedDeferredRevenueRecognitionFromLockedMonthsTooltipItems
	),
	AccruedDeferredAccruedDeferredTooltip: createTooltip(AccruedDeferredAccruedDeferredTooltipItems),

	RetainerPeriodTargetTimeTooltip: createTooltip(RetainerPeriodTargetTimeTooltipItems),
	RetainerPeriodTargetPriceTooltip: createTooltip(RetainerPeriodTargetPriceTooltipItems),

	ProjectOverviewPriorityLevelTooltip: createTooltip(ProjectOverviewPriorityLevelTooltipItems),
	ProjectOverviewWinProbabilityTooltip: createTooltip(ProjectOverviewWinProbabilityTooltipItems),
	ProjectOverviewProjectStartDateTooltip: createTooltip(ProjectOverviewProjectStartDateTooltipItems),
	ProjectOverviewProjectEndDateTooltip: createTooltip(ProjectOverviewProjectEndDateTooltipItems),
	ProjectOverviewClientTooltip: createTooltip(ProjectOverviewClientTooltipItems),
	ProjectOverviewStatusTooltip: createTooltip(ProjectOverviewStatusTooltipItems),
	ProjectOverviewStatusDescriptionTooltip: createTooltip(ProjectOverviewStatusDescriptionTooltipItems),
	ProjectOverviewProjectOwnerTooltip: createTooltip(ProjectOverviewProjectOwnerTooltipItems),
	ProjectOverviewStageTooltip: createTooltip(ProjectOverviewStageTooltipItems),
	ProjectOverviewBudgetTypeTooltip: createTooltip(ProjectOverviewBudgetTypeTooltipItems),
	ProjectOverviewRateCardTooltip: createTooltip(ProjectOverviewRateCardTooltipItems),
	ProjectOverviewProjectProgressTooltip: createTooltip(ProjectOverviewProjectProgressTooltipItems),

	ProjectHoursBaselineHoursTooltip: createTooltip(ProjectHoursBaselineHoursTooltipItems),
	ProjectHoursEstimatedHoursTooltip: createTooltip(ProjectHoursEstimatedHoursTooltipItems),
	ProjectHoursActualHoursTooltip: createTooltip(ProjectHoursActualHoursTooltipItems),
	ProjectHoursRemainingWorkHoursTooltip: createTooltip(ProjectHoursRemainingWorkHoursTooltipItems),
	ProjectHoursProjectedTotalHoursTooltip: createTooltip(ProjectHoursProjectedTotalHoursTooltipItems),
};
